import { useMemo } from "react";
import { REPORT_MACHINE_NAMES } from "../../constants/ReportMacineNames";

type ReportInputBase = {
  ReportName: string;
  ClientTimeZone: string;
};

type DailyLogInput = ReportInputBase & {
  DriverId: string;
  LogDate: string;
};

type DailyLogsInput = ReportInputBase & {
  DateFrom: string;
  DateTo: string;
  DriverId: string;
};

type VehicleMileageSummaryInput = ReportInputBase & {
  DateFrom: string;
  DateTo: string;
  Vehicles: string[];
  StateIds: string[];
};

type VehicleMileageInput = ReportInputBase & {
  DateFrom: string;
  DateTo: string;
  Vehicles: string[];
  StateIds: string[];
};

type LocationHistoryInput = ReportInputBase & {
  DateFrom: string;
  DateTo: string;
  Vehicles: string[];
};

type TripReportDetailInput = ReportInputBase & {
  DateFrom: string;
  DateTo: string;
  Vehicles: string[];
  StateIds: string[];
};

type TripDetailInput = ReportInputBase & {
  DateFrom: string;
  DateTo: string;
  Vehicles: string[];
  StateIds: string[];
};

type IftaMileageInput = ReportInputBase & {
  DateFrom: string;
  DateTo: string;
  Vehicles: string[];
  StateIds: string[];
};

type SafetyReportInput = ReportInputBase & {
  DateFrom: string;
  DateTo: string;
  DriverIds: string[];
};

type DvirHistoryReportInput = ReportInputBase & {
  VehicleId: string;
  DateFrom: string;
  DateTo: string;
  DriverId: string;
};

type EldEventsReportInput = ReportInputBase & {
  DateFrom: string;
  DateTo: string;
  Vehicles: string[];
};

type LocationsHistoryReportInput = ReportInputBase & {
  DateFrom: string;
  DateTo: string;
  Vehicles: string[];
};

type VehicleInactivityReportInput = ReportInputBase & {
  DateFrom: string;
  DateTo: string;
  Vehicles: string[];
};

type VehicleDistanceReportInput = ReportInputBase & {
  DateFrom: string;
  DateTo: string;
  Vehicles: string[];
};

type EldDeviceUsageReportInput = ReportInputBase & {
  DateFrom: string;
  DateTo: string;
  TenantId: string;
  DeviceType?: string | number;
  CustomerOwned?: boolean;
  ReportFormat?: string | number;
};

type ReportInput =
  | DailyLogInput
  | DailyLogsInput
  | VehicleMileageSummaryInput
  | VehicleMileageInput
  | LocationHistoryInput
  | TripReportDetailInput
  | TripDetailInput
  | IftaMileageInput
  | SafetyReportInput
  | DvirHistoryReportInput
  | EldEventsReportInput
  | LocationsHistoryReportInput
  | VehicleInactivityReportInput
  | VehicleDistanceReportInput
  | EldDeviceUsageReportInput;

type Props = {
  machineName?: string | null;
  reportInput?: string | null;
  reportType?: string | null;
  targetTenantName?: string | null;
};

const ReportDetails = ({
  machineName,
  reportInput,
}: Props) => {
  const parsedInput = useMemo(() => {
    try {
      return JSON.parse(reportInput ?? "") as ReportInput;
    } catch {
      console.error("Invalid report input format");
      return null;
    }
  }, [reportInput]);

  if (!parsedInput) {
    return <div>Error: Invalid report data</div>;
  }

  const renderDetails = () => {
    switch (machineName) {
      case REPORT_MACHINE_NAMES.DAILY_LOG: {
        const { DriverId, LogDate } = parsedInput as DailyLogInput;

        if (!DriverId || !LogDate) return "Uknown data";

        return (
          <p>
            The daily log for {new Date(LogDate).toLocaleDateString()} for the
            driver with ID: {DriverId}.
          </p>
        );
      }

      case REPORT_MACHINE_NAMES.DAILY_LOGS: {
        const { DriverId, DateFrom, DateTo } = parsedInput as DailyLogsInput;

        if (!DriverId || !DateFrom || !DateTo) return "Uknown data";

        return (
          <div>
            <p>
              From {new Date(DateFrom).toLocaleDateString()} to{" "}
              {new Date(DateTo).toLocaleDateString()} for the driver with ID:{" "}
              {DriverId}.
            </p>
          </div>
        );
      }

      case REPORT_MACHINE_NAMES.VEHICLE_MILEAGE_SUMMARY: {
        const {
          DateFrom,
          DateTo,
          Vehicles,
          StateIds,
        } = parsedInput as VehicleMileageSummaryInput;

        if (!Vehicles || !StateIds || !DateFrom || !DateTo)
          return "Uknown data";

        return (
          <p>
            From {new Date(DateFrom).toLocaleDateString()} to{" "}
            {new Date(DateTo).toLocaleDateString()}, for {Vehicles.length}{" "}
            vehicle(s) and {StateIds.length} state(s).
          </p>
        );
      }

      case REPORT_MACHINE_NAMES.VEHICLE_MILEAGE: {
        const {
          DateFrom,
          DateTo,
          Vehicles,
          StateIds,
        } = parsedInput as VehicleMileageInput;

        if (!Vehicles || !StateIds || !DateFrom || !DateTo)
          return "Uknown data";

        return (
          <p>
            From {new Date(DateFrom).toLocaleDateString()} to{" "}
            {new Date(DateTo).toLocaleDateString()}, for {Vehicles.length}{" "}
            vehicle(s) and {StateIds.length} state(s).
          </p>
        );
      }

      case REPORT_MACHINE_NAMES.LOCATION_HISTORY: {
        const {
          DateFrom,
          DateTo,
          Vehicles,
        } = parsedInput as LocationHistoryInput;

        if (!Vehicles || !DateFrom || !DateTo) return "Uknown data";

        return (
          <p>
            From {new Date(DateFrom).toLocaleDateString()} to{" "}
            {new Date(DateTo).toLocaleDateString()}, for {Vehicles.length}{" "}
            vehicle(s).
          </p>
        );
      }

      case REPORT_MACHINE_NAMES.TRIP_REPORT_DETAIL: {
        const {
          DateFrom,
          DateTo,
          Vehicles,
          StateIds,
        } = parsedInput as TripReportDetailInput;

        if (!Vehicles || !StateIds || !DateFrom || !DateTo)
          return "Uknown data";

        return (
          <p>
            From {new Date(DateFrom).toLocaleDateString()} to{" "}
            {new Date(DateTo).toLocaleDateString()}, for {Vehicles.length}{" "}
            vehicle(s) and {StateIds.length} state(s).
          </p>
        );
      }

      case REPORT_MACHINE_NAMES.TRIP_DETAIL: {
        const {
          DateFrom,
          DateTo,
          Vehicles,
          StateIds,
        } = parsedInput as TripDetailInput;

        if (!Vehicles || !StateIds || !DateFrom || !DateTo)
          return "Uknown data";

        return (
          <p>
            From {new Date(DateFrom).toLocaleDateString()} to{" "}
            {new Date(DateTo).toLocaleDateString()}, for {Vehicles.length}{" "}
            vehicle(s) and {StateIds.length} state(s).
          </p>
        );
      }

      case REPORT_MACHINE_NAMES.IFTA_SUMMARY: {
        const {
          DateFrom,
          DateTo,
          Vehicles,
          StateIds,
        } = parsedInput as IftaMileageInput;

        if (!Vehicles || !StateIds || !DateFrom || !DateTo)
          return "Uknown data";

        return (
          <p>
            From {new Date(DateFrom).toLocaleDateString()} to{" "}
            {new Date(DateTo).toLocaleDateString()}, for {Vehicles.length}{" "}
            vehicle(s) and {StateIds.length} state(s).
          </p>
        );
      }

      case REPORT_MACHINE_NAMES.IFTA_MILEAGE_DETAIL: {
        const {
          DateFrom,
          DateTo,
          Vehicles,
          StateIds,
        } = parsedInput as IftaMileageInput;

        if (!Vehicles || !StateIds || !DateFrom || !DateTo)
          return "Uknown data";

        return (
          <p>
            From {new Date(DateFrom).toLocaleDateString()} to{" "}
            {new Date(DateTo).toLocaleDateString()}, for {Vehicles.length}{" "}
            vehicle(s) and {StateIds.length} state(s).
          </p>
        );
      }

      case REPORT_MACHINE_NAMES.IFTA_MILEAGE: {
        const {
          DateFrom,
          DateTo,
          Vehicles,
          StateIds,
        } = parsedInput as IftaMileageInput;

        if (!Vehicles || !StateIds || !DateFrom || !DateTo)
          return "Uknown data";

        return (
          <p>
            From {new Date(DateFrom).toLocaleDateString()} to{" "}
            {new Date(DateTo).toLocaleDateString()}, for {Vehicles.length}{" "}
            vehicle(s) and {StateIds.length} state(s).
          </p>
        );
      }

      case REPORT_MACHINE_NAMES.SAFETY: {
        const {
          DateFrom,
          DateTo,
          DriverIds,
        } = parsedInput as SafetyReportInput;

        if (!DriverIds || !DateFrom || !DateTo) return "Uknown data";

        return (
          <p>
            From {new Date(DateFrom).toLocaleDateString()} to{" "}
            {new Date(DateTo).toLocaleDateString()}, for {DriverIds.length}{" "}
            driver(s).
          </p>
        );
      }

      case REPORT_MACHINE_NAMES.DVIR_HISTORY: {
        const {
          VehicleId,
          DateFrom,
          DateTo,
          DriverId,
        } = parsedInput as DvirHistoryReportInput;

        if (!VehicleId || !DateFrom || !DateTo || !DriverId)
          return "Uknown data";

        return (
          <p>
            From {new Date(DateFrom).toLocaleDateString()} to{" "}
            {new Date(DateTo).toLocaleDateString()}, for the vehicle with ID:{" "}
            {VehicleId} and the driver with ID: {DriverId}.
          </p>
        );
      }

      case REPORT_MACHINE_NAMES.ELD_EVENTS: {
        const {
          DateFrom,
          DateTo,
          Vehicles,
        } = parsedInput as EldEventsReportInput;

        if (!Vehicles || !DateFrom || !DateTo) return "Uknown data";

        return (
          <p>
            From {new Date(DateFrom).toLocaleDateString()} to{" "}
            {new Date(DateTo).toLocaleDateString()}, for {Vehicles.length}{" "}
            vehicle(s).
          </p>
        );
      }

      case REPORT_MACHINE_NAMES.LOCATIONS_HISTORY: {
        const {
          DateFrom,
          DateTo,
          Vehicles,
        } = parsedInput as LocationsHistoryReportInput;

        if (!Vehicles || !DateFrom || !DateTo) return "Uknown data";

        return (
          <p>
            From {new Date(DateFrom).toLocaleDateString()} to{" "}
            {new Date(DateTo).toLocaleDateString()}, for {Vehicles.length}{" "}
            vehicle(s).
          </p>
        );
      }

      case REPORT_MACHINE_NAMES.VEHICLE_INACTIVITY: {
        const {
          DateFrom,
          DateTo,
          Vehicles,
        } = parsedInput as VehicleInactivityReportInput;

        if (!Vehicles || !DateFrom || !DateTo) return "Uknown data";

        return (
          <p>
            From {new Date(DateFrom).toLocaleDateString()} to{" "}
            {new Date(DateTo).toLocaleDateString()}, for {Vehicles.length}{" "}
            vehicle(s).
          </p>
        );
      }

      case REPORT_MACHINE_NAMES.VEHICLE_DISTANCE: {
        const {
          DateFrom,
          DateTo,
          Vehicles,
        } = parsedInput as VehicleDistanceReportInput;

        if (!Vehicles || !DateFrom || !DateTo) return "Uknown data";

        return (
          <p>
            From {new Date(DateFrom).toLocaleDateString()} to{" "}
            {new Date(DateTo).toLocaleDateString()}, for {Vehicles.length}{" "}
            vehicle(s).
          </p>
        );
      }
      case REPORT_MACHINE_NAMES.ELD_DEVICE_USAGE_FROM_EVENTS: {
        const { DateFrom, DateTo } = parsedInput as EldDeviceUsageReportInput;

        if (!DateFrom || !DateTo) return "Unknown data";

        return (
          <p>
            From {new Date(DateFrom).toLocaleDateString()} to{" "}
            {new Date(DateTo).toLocaleDateString()}.
          </p>
        );
      }
      default:
        return <div>Unknown report type</div>;
    }
  };

  return <div className="report-details">{renderDetails()}</div>;
};

export default ReportDetails;
